import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import ConditionalLink from '@plone/volto/components/manage/ConditionalLink/ConditionalLink';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { MEDIA_PAGE_PREVIEW_ROUTE } from 'volto-base-addon/constants';
import { isInternalURL } from '@plone/volto/helpers/Url/Url';
import request from 'superagent';
import config from '@plone/volto/registry';
import Api from '@plone/volto/helpers/Api/Api';
import DefaultImageSVG from '@plone/volto/components/manage/Blocks/Listing/default-image.svg';
import LivePreviewSVG from './preview_live.png';
import AudioPreviewSVG from './preview_audio.svg';
import FilePreviewSVG from './preview_file.svg';
import styles from './style.module.scss';
import { userHasRoles } from '@plone/volto/helpers/User/User';

import lockIcon from '@plone/volto/icons/lock.svg';
import hideIcon from '@plone/volto/icons/hide.svg';
import Icon from '@plone/volto/components/theme/Icon/Icon';

const messages = defineMessages({
  unknown: {
    id: 'unknown',
    defaultMessage: 'Unknown',
  },
  published: {
    id: 'published_date',
    defaultMessage: 'Published:',
  },
  created: {
    id: 'created_date',
    defaultMessage: 'Created:',
  },
});

const Item = ({ user, item, images }) => {
  const intl = useIntl();
  let { image, hoverImage } = images;
  if (hoverImage === null) {
    hoverImage = image;
  }
  const [previewOn, setPreviewOn] = useState(false);

  const getDateInfo = (item) => {
    const effective = item.effective;
    const created = item.created;

    const epochStart = new Date(0);
    const isEffective =
      effective !== undefined && new Date(effective) > epochStart;
    const rawDate = isEffective ? effective : created;

    if (!rawDate) {
      return { label: messages.published, date: null };
    }

    const parsedDate = new Date(rawDate);
    if (isNaN(parsedDate.getTime())) {
      return { label: messages.published, date: null };
    }

    const timeZone = config.settings.dateFormatTimeZone;
    return {
      label: isEffective ? messages.published : messages.created,
      date: intl.formatDate(parsedDate, timeZone ? { timeZone } : undefined),
    };
  };

  const getMediaDuration = () => {
    const endTimeInSeconds = images?.endTime;

    if (typeof endTimeInSeconds !== 'number') return null;

    const totalSeconds = Math.round(endTimeInSeconds);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = String(totalSeconds % 60).padStart(2, '0');

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`;
    } else {
      return `${minutes}:${seconds}`;
    }
  };

  const isPrivateItem = (item) => {
    return item.review_state === 'private';
  };

  const isLockedItem = (item) => {
    return item.pathkey_required;
  };

  return (
    <div
      className={`${styles.gridItem} Grid-view mediapage-item`}
      onMouseEnter={() => {
        setPreviewOn(true);
      }}
      onMouseLeave={() => {
        setPreviewOn(false);
      }}
    >
      <div className={`${styles.imagewrapper} mediapage-img-wrapper`}>
        {((userHasRoles(user, ['Manager']) && isPrivateItem(item)) ||
          isLockedItem(item)) && (
          <div
            style={{
              position: 'absolute',
              borderBottomRightRadius: '12px',
              backgroundColor: '#dcdcdcc4',
              filter: 'drop-shadow(1px 1px 0px grey)',
              borderTopLeftRadius: '11px',
            }}
          >
            <div style={{ display: 'flex' }}>
              {userHasRoles(user, ['Manager']) && isPrivateItem(item) && (
                <i
                  aria-hidden="true"
                  className="icon"
                  style={{
                    width: '100%',
                    height: '100%',
                    margin: 0,
                    padding: '5px',
                  }}
                >
                  <Icon name={hideIcon} size="18px" color="red" />
                </i>
              )}
              {isLockedItem(item) && (
                <i
                  aria-hidden="true"
                  className="icon"
                  style={{
                    width: '100%',
                    height: '100%',
                    margin: 0,
                    padding: '5px',
                  }}
                >
                  <Icon name={lockIcon} size="18px" />
                </i>
              )}
            </div>
          </div>
        )}
        <img
          className={`${styles.mediapagePreviewImg} mediapage-preview-img`}
          src={!previewOn ? image : hoverImage}
          alt={`${item.title} preview`}
        ></img>
        {getMediaDuration() && (
          <span className={`${styles.time}`}>{getMediaDuration()}</span>
        )}
      </div>
      <div className={`${styles.listingBody} listing-body`}>
        <h3 title={item.title || item.id} className={`${styles.title}`}>
          {item.title || item.id}
        </h3>

        <div className={`${styles.videoinformation}`}>
          <p className={`${styles.videoinfotext}`}>
            <FormattedMessage {...getDateInfo(item).label} />{' '}
            {getDateInfo(item).date || (
              <FormattedMessage {...messages.unknown} />
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

const MediaPageSummaryGridTemplate = ({
  items,
  linkTitle,
  linkHref,
  isEditMode,
}) => {
  let link = null;
  let href = linkHref?.[0]?.['@id'] || '';
  const [previews, setPreviews] = useState({});
  const { token } = useSelector((state) => state.userSession);
  const user = useSelector((state) => state.users.user);

  if (isInternalURL(href)) {
    link = (
      <ConditionalLink to={flattenToAppURL(href)} condition={!isEditMode}>
        {linkTitle || href}
      </ConditionalLink>
    );
  } else if (href) {
    link = <a href={href}>{linkTitle || href}</a>;
  }

  const getDefaultImages = (item) => {
    if (item['@type'] === 'media_page') {
      return { image: DefaultImageSVG, hoverImage: null };
    }
    if (item['@type'] === 'File') {
      return { image: FilePreviewSVG, hoverImage: null };
    }
    return { image: DefaultImageSVG, hoverImage: null };
  };

  useEffect(() => {
    const api = new Api();
    const fetchItemPreview = async (item) => {
      try {
        const data = await api.get(flattenToAppURL(item['@id']), {
          headers: { Authorization: `Bearer ${token}` },
        });

        let endTimeInSeconds = null;

        // Use media_duration from catalog brain (via listing item) first,
        // fall back to the fetched content's media_duration field.
        if (typeof item.media_duration === 'number') {
          endTimeInSeconds = item.media_duration;
        } else if (typeof data.media_duration === 'number') {
          endTimeInSeconds = data.media_duration;
        }

        let previewDetails = {
          image: getDefaultImages(item).image,
          hoverImage: null,
        };

        if (data.poster) {
          previewDetails = {
            image: `data:${data.poster['content-type']};base64,${data.poster['data']}`,
            hoverImage: null,
          };
        } else if (data.image) {
          const image = data.image.scales?.preview
            ? data.image.scales.preview.download
            : data.image.download;
          previewDetails = { image, hoverImage: null };
        } else if (item['@type'] === 'media_page') {
          // If the returned S3 preview values are relative, get their presigned URLs.
          if (
            data.s3_image_preview &&
            !data.s3_image_preview.includes('http')
          ) {
            const { body: imagePreview } = await request.get(
              `${config.settings.publicPath}/${MEDIA_PAGE_PREVIEW_ROUTE}/${item.UID}/${data.s3_image_preview}`,
            );
            data.s3_image_preview = imagePreview['presigned_url'];
          }
          if (
            data.s3_video_preview &&
            !data.s3_video_preview.includes('http')
          ) {
            const { body: videoPreview } = await request.get(
              `${config.settings.publicPath}/${MEDIA_PAGE_PREVIEW_ROUTE}/${item.UID}/${data.s3_video_preview}`,
            );
            data.s3_video_preview = videoPreview['presigned_url'];
          }

          // Now, set previewDetails with the updated values.
          if (data.s3_image_preview || data.s3_video_preview) {
            previewDetails = {
              image: data.s3_image_preview || DefaultImageSVG,
              hoverImage: data.s3_video_preview,
            };
          } else if (data.s3_object_id) {
            previewDetails = { image: AudioPreviewSVG, hoverImage: null };
          } else if (item.live_state === 'active') {
            previewDetails = { image: LivePreviewSVG, hoverImage: null };
          } else {
            previewDetails = { image: DefaultImageSVG, hoverImage: null };
          }
        }
        previewDetails.endTime = endTimeInSeconds;
        setPreviews((prev) => ({ ...prev, [item['@id']]: previewDetails }));
      } catch (e) {
        if (e.status === 403) {
          // Gracefully set default preview for a 403 response.
          setPreviews((prev) => ({
            ...prev,
            [item['@id']]: getDefaultImages(item),
          }));
        } else if (e.status === 404) {
          // Gracefully set default preview for a 404 response.
          setPreviews((prev) => ({
            ...prev,
            [item['@id']]: getDefaultImages(item),
          }));
        }
      }
    };

    items.forEach((item) => {
      fetchItemPreview(item);
    });
  }, [items, token]);

  return (
    <>
      <div className={`${styles.grid} items mediapage-summary-template`}>
        {items.map((item) => (
          <div className={`${styles.listingItem} `} key={item['@id']}>
            <ConditionalLink
              className={`${styles.listingLink}`}
              item={item}
              condition={!isEditMode}
            >
              <Item
                item={item}
                images={previews[item['@id']] || getDefaultImages(item)}
                user={user}
              />
            </ConditionalLink>
          </div>
        ))}
      </div>

      {link && <div className="footer">{link}</div>}
    </>
  );
};

MediaPageSummaryGridTemplate.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
  linkMore: PropTypes.any,
  isEditMode: PropTypes.bool,
};

export default MediaPageSummaryGridTemplate;
